import {skipToken, useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {Brand, MediaType} from 'bigdatr-style';
import {OnboardingSearchInput} from '~/graphql-react-query/graphql';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {FilterQuery} from '../filter/graphqlQueries';
import {Search, OnBoardingSearch} from './graphqlQueries';

export function useAdvertiserSearch(query: string, options?: {enabled: boolean}) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: ['advertiserSearch', query],
        queryFn: enabled
            ? async () => {
                  const response = await request(Search, {
                      search: {
                          type: 'brand',
                          limitToViewerIndustries: false,
                          query,
                          pageNum: 1,
                          pageSize: 25
                      }
                  });
                  if (!response.searchV2?.search.items)
                      throw new Error('No advertisers returned from searchV2');

                  const rawEntities = response.searchV2?.search.items;
                  const brands = rawEntities.flatMap((entity) => {
                      if (entity.type === 'brand') return [new Brand(entity)];
                      return [];
                  });
                  return brands;
              }
            : skipToken
    });
}

export function useOnboardingSearch(payload: OnboardingSearchInput) {
    const request = useClientMainAuthorizedFetch();

    return useInfiniteQuery({
        queryKey: ['onBoardingSearch', payload],
        queryFn: async ({pageParam}) => {
            const response = await request(OnBoardingSearch, {
                search: {
                    query: payload.query,
                    industries: payload.industries,
                    industryIds: payload.industryIds,
                    pageNum: pageParam,
                    includeCategories: payload.includeCategories,
                    pageSize: payload.pageSize,
                    useMediaValueV3: true
                }
            });
            const rawResponse = response.searchV2?.onboardingSearch;
            if (!rawResponse) throw new Error('OnBoarding search list not returned from api');

            return {
                items: rawResponse.items,
                pageInfo: rawResponse.pageInfo
            };
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const hasNextPage = lastPage.pageInfo?.hasNextPage;
            const currentPage = lastPage.pageInfo?.currentPage;
            if (hasNextPage && currentPage) return currentPage + 1;
            return null;
        }
    });
}

export function useFetchAllIndustries(includeArchived = false) {
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: ['allIndustries', includeArchived],
        queryFn: async () => {
            const response = await request(Search, {
                search: {
                    type: 'industry',
                    limitToViewerIndustries: false,
                    pageSize: 1000,
                    includeArchived
                }
            });
            if (!response.searchV2?.search.items)
                throw new Error('No industries returned from searchV2');

            const rawEntities = response.searchV2?.search.items;
            return rawEntities;
        }
    });
}

export function useFetchAllMediaTypes() {
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: ['allMediaTypes'],
        queryFn: async () => {
            const response = await request(FilterQuery, {
                input: {
                    // arbirtrary page size to get all media types
                    pageSize: 50,
                    column: 'mediaType'
                }
            });
            const data = response.filterV2?.filters?.items.mediaType;
            if (!data || data.length === 0) throw new Error('No media types returned');

            return (
                data
                    // the business doesn't want to see Classified media types in the customer facing app
                    .filter(({canonicalName}) => {
                        return canonicalName !== 'Classified';
                    })
                    .map((mediaType) => new MediaType(mediaType))
                    .sort((a, b) => a.label.localeCompare(b.label))
            );
        }
    });
}
